import * as React from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, Chip,
  CircularProgress, Divider, FormControlLabel, Grid, Paper, TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import api from '../services/api';

export default function TabProcedimento({ hostOverview, overviewLoading, props }) {
  const [stepsStates, setStepsStates] = React.useState({});
  const [execLoading, setExecLoading] = React.useState(false);
  const [executions, setExecutions] = React.useState([]);

  const ppis = hostOverview?.ppis || [];

  // Legacy fallback: if no ppis array but has ppi_id
  const legacyPpi = !ppis.length && hostOverview?.ppi_id ? [{
    id: hostOverview.ppi_id,
    description: 'Procedimento',
    weight: 0,
    validated: hostOverview.ppi_validated,
    matched_by: 'tag',
    steps: [],
    attachments: hostOverview.ppi_attachments || [],
  }] : null;

  const activePpis = legacyPpi || ppis;

  // Initialize steps state per PPI
  React.useEffect(() => {
    const newStates = {};
    activePpis.forEach(ppi => {
      if (ppi.steps?.length > 0) {
        newStates[ppi.id] = ppi.steps.map(s => ({
          step_id: s.id,
          title: s.title || '',
          checked: false,
          checked_at: null,
          observation: '',
          required: s.required || false,
          content: s.content || '',
        }));
      }
    });
    setStepsStates(newStates);
  }, [hostOverview]);

  // Fetch executions
  React.useEffect(() => {
    if (!props.eventId || !props.codEmpresa || Array.isArray(props.eventId)) return;
    api.get(`ppi/execution/find/${props.eventId}/${props.codEmpresa}`)
      .then(res => setExecutions(res.data || []))
      .catch(() => setExecutions([]));
  }, [props.eventId, props.codEmpresa]);

  const handleCheckToggle = (ppiId, idx) => {
    setStepsStates(prev => ({
      ...prev,
      [ppiId]: prev[ppiId].map((s, i) =>
        i === idx ? { ...s, checked: !s.checked, checked_at: !s.checked ? new Date().toISOString() : null } : s
      ),
    }));
  };

  const handleObsChange = (ppiId, idx, value) => {
    setStepsStates(prev => ({
      ...prev,
      [ppiId]: prev[ppiId].map((s, i) => i === idx ? { ...s, observation: value } : s),
    }));
  };

  const handleSaveExecution = (ppiId) => {
    setExecLoading(true);
    const ppiSteps = stepsStates[ppiId] || [];
    const allRequired = ppiSteps.filter(s => s.required).every(s => s.checked);
    const allChecked = ppiSteps.every(s => s.checked);

    api.post('ppi/execution/create/', {
      ppi_id: ppiId,
      event_id: props.eventId,
      cod_empresa: props.codEmpresa,
      checklist_state: [],
      steps_state: ppiSteps.map(({ content, ...rest }) => rest),
      completed: allRequired && allChecked,
      observation: null,
    })
      .then(res => {
        setExecutions(prev => [{
          ...res.data,
          steps_state: ppiSteps,
          ppi_id: ppiId,
          created_at: new Date().toISOString(),
        }, ...prev]);
      })
      .catch(() => {})
      .finally(() => setExecLoading(false));
  };

  const handleOpenAttachment = (att) => {
    if (att.s3_key) {
      window.open(`${api.defaults.baseURL}/ppi/attachments/media/?key=${encodeURIComponent(att.s3_key)}`, '_blank');
    } else {
      api.get(`ppi/attachments/url/${att.id}`)
        .then(res => { if (res.data?.url) window.open(res.data.url, '_blank'); })
        .catch(() => {});
    }
  };

  const getFileIcon = (contentType) => {
    if (!contentType) return <InsertDriveFileIcon fontSize="small" />;
    if (contentType.startsWith('image/')) return <ImageIcon fontSize="small" />;
    if (contentType.startsWith('video/')) return <VideoLibraryIcon fontSize="small" />;
    return <InsertDriveFileIcon fontSize="small" />;
  };

  if (overviewLoading) {
    return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
  }

  if (!activePpis.length && !hostOverview?.ppi) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Nenhuma PPI vinculada a este alarme.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {/* Left: PPI Steps (70%) */}
      <Grid item xs={12} md={8}>
        {/* Legacy text PPI */}
        {hostOverview?.ppi && !ppis.length && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
            <Typography variant="subtitle2" gutterBottom>Procedimento</Typography>
            {hostOverview.ppi.split('\n').map((line, i) => (
              <Typography key={i} variant="body2">{line}</Typography>
            ))}
          </Paper>
        )}

        {/* PPIs with steps */}
        {activePpis.map((ppi, ppiIdx) => (
          <Accordion key={ppi.id} defaultExpanded={ppiIdx === 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography variant="subtitle2">{ppi.description}</Typography>
                <Chip label={`w:${ppi.weight}`} size="small" variant="outlined" />
                <Chip label={ppi.matched_by} size="small" color={ppi.matched_by === 'tag' ? 'primary' : 'secondary'} />
                {!ppi.validated && <Chip label="Nao validada" size="small" color="warning" variant="outlined" />}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {/* Steps */}
              {(stepsStates[ppi.id] || []).map((step, idx) => (
                <Paper key={step.step_id || idx} variant="outlined" sx={{ p: 1.5, mb: 1, bgcolor: step.checked ? 'action.selected' : 'inherit' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Checkbox
                      checked={step.checked}
                      onChange={() => handleCheckToggle(ppi.id, idx)}
                      size="small"
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {step.title || `Step ${idx + 1}`}
                        </Typography>
                        {step.required && (
                          <Chip label="obrigatorio" size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                      <Box
                        sx={{
                          '& img': { maxWidth: '100%', maxHeight: 400, objectFit: 'contain', cursor: 'pointer', borderRadius: 1 },
                          '& video': { maxWidth: '100%', maxHeight: 400, borderRadius: 1 },
                          '& pre': { bgcolor: '#f5f5f5', p: 1, borderRadius: 1, overflow: 'auto' },
                          fontSize: '0.875rem',
                        }}
                        dangerouslySetInnerHTML={{ __html: step.content }}
                      />
                      <TextField
                        fullWidth size="small" placeholder="Observacao (opcional)"
                        value={step.observation}
                        onChange={(e) => handleObsChange(ppi.id, idx, e.target.value)}
                        sx={{ mt: 1 }}
                        multiline maxRows={3}
                      />
                    </Box>
                  </Box>
                </Paper>
              ))}

              {/* Attachments */}
              {ppi.attachments?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    <AttachFileIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Anexos ({ppi.attachments.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {ppi.attachments.map(att => (
                      <Chip
                        key={att.id}
                        icon={getFileIcon(att.content_type)}
                        label={att.filename}
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenAttachment(att)}
                        onDelete={() => handleOpenAttachment(att)}
                        deleteIcon={<OpenInNewIcon fontSize="small" />}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Save execution */}
              {(stepsStates[ppi.id] || []).length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                  <Button
                    size="small" variant="contained" startIcon={<SaveIcon />}
                    onClick={() => handleSaveExecution(ppi.id)}
                    disabled={execLoading}
                  >
                    {execLoading ? <CircularProgress size={18} /> : 'Salvar Execucao'}
                  </Button>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Execucoes anteriores */}
        {executions.length > 0 && (
          <Paper variant="outlined" sx={{ p: 1.5, mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Execucoes anteriores</Typography>
            <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
              {executions.map((exec, i) => (
                <Alert
                  key={exec.id || i}
                  severity={exec.completed ? 'success' : 'info'}
                  sx={{ mb: 0.5, py: 0 }}
                  icon={false}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary">
                      {exec.user_name} - {new Date(exec.created_at).toLocaleString('pt-BR')}
                    </Typography>
                    {exec.ppi_description && (
                      <Chip label={exec.ppi_description} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                    )}
                    <Chip
                      label={exec.completed ? 'Completa' : 'Parcial'}
                      size="small"
                      color={exec.completed ? 'success' : 'warning'}
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  </Box>
                  {exec.steps_state && (
                    <Box sx={{ mt: 0.5, pl: 1 }}>
                      {exec.steps_state.map((s, si) => (
                        <Typography key={si} variant="caption" display="block">
                          {s.checked ? '✓' : '○'} {s.title || `Step ${si + 1}`}
                          {s.observation && <span style={{ color: '#666' }}> — {s.observation}</span>}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Alert>
              ))}
            </Box>
          </Paper>
        )}
      </Grid>

      {/* Right: Alarm info (30%) */}
      <Grid item xs={12} md={4}>
        <Paper variant="outlined" sx={{ p: 1.5, position: 'sticky', top: 16 }}>
          <Typography variant="subtitle2" gutterBottom>Info do Alarme</Typography>
          <InfoRow label="Host" value={props.hostname} />
          <InfoRow label="IP" value={props.ip} />
          <InfoRow label="Source" value={props.source || 'zabbix'} />
          <InfoRow label="Severidade" value={props.severity} />
          <InfoRow label="Horario" value={props.clock} />
          {props.detailAlarms && <InfoRow label="Descricao" value={props.detailAlarms} />}

          {hostOverview?.obs_dash && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">Obs empresa:</Typography>
              <Typography variant="body2">{hostOverview.obs_dash}</Typography>
            </>
          )}

          {/* Wazuh extra info */}
          {(props.source === 'wazuh') && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">Wazuh</Typography>
              {props.srcip && <InfoRow label="SrcIP" value={props.srcip} />}
              {props.ruleId && <InfoRow label="Rule ID" value={props.ruleId} />}
              {props.incidentType && <InfoRow label="Tipo" value={props.incidentType} />}
            </>
          )}

          {/* Host overview summary */}
          {hostOverview?.summary && hostOverview.summary.total_events > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">Historico ({hostOverview.summary.total_events} eventos)</Typography>
              <InfoRow label="Ativos" value={hostOverview.summary.active_count} />
              <InfoRow label="Resolvidos" value={hostOverview.summary.resolved_count} />
              <InfoRow label="Max Sev" value={hostOverview.summary.max_severity} />
            </>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}

function InfoRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
      <Typography variant="caption" color="text.secondary">{label}:</Typography>
      <Typography variant="caption" fontWeight={500} sx={{ maxWidth: '60%', textAlign: 'right', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );
}
